import type { Logistics, TravelDraft, TravelItem } from '@wr/shared';
import { ApiError, get } from '../../api.js';
import type { LocalStore } from '../../offline/store.js';
import { isUnreachable } from '../people/data.js';

export type TripEvent = {
  id: string;
  series: string;
  year: number;
  event_code: string;
  location: string | null;
  starts_on: string | null;
  ends_on: string | null;
  status: string;
};

/** A trip of My trips (or of another team, for the team view). */
export interface Trip {
  event: TripEvent;
  mine: boolean;
  team: { id: string; name: string }[];
  outbound: TravelItem | null;
  gaps: string[];
}

export interface TeamMember {
  id: string;
  name: string;
  mobile: string | null;
  arrival: string | null;
  gaps: string[];
}

export interface TravelDocument {
  id: string;
  name: string;
  file_type: string | null;
  size_bytes: number | null;
}

/** The trip page, as the device keeps it. */
export interface TripPage {
  event: TripEvent;
  onTeam: boolean;
  canAdd: boolean;
  logistics: Logistics;
  documents: TravelDocument[];
  team: TeamMember[];
  items: TravelItem[];
  previous: { name: string; fields: Logistics['fields'] } | null;
  /** Part b: the person's confirmations read (or being read) into drafts. */
  drafts: TravelDraft[];
  /** Where to forward this show's confirmations; null until it is set up. */
  inboundAddress: string | null;
}

const CACHE = 'wr-travel';
export const itemFileUrl = (id: string) => `/api/travel/items/${id}/file`;
export const documentUrl = (id: string) => `/api/travel/documents/${id}/file`;

/**
 * From the portal when it answers, kept on the device; without network,
 * what the device kept (brief section 7.3: available offline once opened).
 */
async function kept<T>(store: LocalStore | null, key: string, path: string) {
  try {
    const value = await get<T>(path);
    await store?.setMeta(key, value).catch(() => undefined);
    return { value, offline: false };
  } catch (error) {
    if (!store || !isUnreachable(error)) throw error;
    const value = await store.getMeta<T>(key).catch(() => null);
    if (value === null || value === undefined) throw error;
    return { value, offline: true };
  }
}

export async function loadTrips(store: LocalStore | null) {
  const { value, offline } = await kept<{ trips: Trip[] }>(store, 'travel:trips', '/travel');
  return { trips: value.trips, offline };
}

export async function loadTrip(store: LocalStore | null, eventId: string) {
  const { value, offline } = await kept<TripPage>(
    store,
    `travel:${eventId}`,
    `/travel/events/${encodeURIComponent(eventId)}`,
  );
  if (!offline) void keepFiles(value);
  // A page kept by an earlier version of the portal has no drafts yet.
  return {
    page: { ...value, drafts: value.drafts ?? [], inboundAddress: value.inboundAddress ?? null },
    offline,
  };
}

/** The trip's files (badges, plans, documents), kept for a show without network. */
async function keepFiles(page: TripPage) {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(CACHE);
    const urls = [
      ...page.items.filter((i) => i.has_file).map((i) => itemFileUrl(i.id)),
      ...page.documents.map((d) => documentUrl(d.id)),
    ];
    for (const url of urls) {
      if (await cache.match(url)) continue;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (res.ok) await cache.put(url, res);
    }
  } catch {
    // The cache is full or unavailable: the files then need the network.
  }
}

/** Opens a kept file, else the portal's, in a new tab. */
export async function openFile(url: string) {
  const win = window.open('', '_blank');
  let target = url;
  if (typeof caches !== 'undefined') {
    const copy = await caches.match(url).catch(() => undefined);
    if (copy) target = URL.createObjectURL(await copy.blob());
  }
  if (win) win.location.href = target;
  else window.location.href = target;
}

/** Saves a file made on the device (Add to calendar). */
export function saveText(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "Sun, Oct 25, 18:30" from a confirmation's local time (no time zone). */
export function fmtDateTime(value: string | undefined | null): string {
  if (!value) return '';
  const [date = '', time] = value.split('T');
  const day = new Date(`${date}T12:00:00Z`).toLocaleDateString('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return time ? `${day}, ${time.slice(0, 5)}` : day;
}

/** "Mon, Oct 26". */
export function fmtDay(value: string | undefined | null): string {
  return value ? fmtDateTime(value.slice(0, 10)) : '';
}

/** Today on this device, as YYYY-MM-DD. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const mapLink = (address: string) =>
  `https://maps.google.com/?q=${encodeURIComponent(address)}`;

/**
 * Waits for a draft to be read by the worker (a few seconds; up to two
 * minutes when the AI is slow and tries again).
 */
export async function waitForDraft(
  id: string,
  options: { everyMs?: number; forMs?: number; cancelled?: () => boolean } = {},
): Promise<TravelDraft> {
  const { everyMs = 1500, forMs = 120_000, cancelled } = options;
  const until = Date.now() + forMs;
  for (;;) {
    const draft = await get<TravelDraft>(`/travel/drafts/${id}`);
    if (draft.status !== 'waiting') return draft;
    if (Date.now() > until || cancelled?.()) throw new ApiError(504, 'unavailable');
    await new Promise((resolve) => setTimeout(resolve, everyMs));
  }
}

/** Someone an admin can put on a show's team (Nov 2, 2026: invited people too). */
export interface TeamCandidate {
  id: string;
  name: string;
  invitation?: 'active' | 'pending' | 'expired';
}

/** "Giulia Conti (Invitation pending)": an invited person who has not activated the account yet. */
export function candidateLabel(p: TeamCandidate): string {
  if (p.invitation === 'pending') return `${p.name} (Invitation pending)`;
  if (p.invitation === 'expired') return `${p.name} (Invitation expired: send it again first)`;
  return p.name;
}
